// src/services/notificationIntegrationService.js
import notificationDispatcher from './NotificationDispatcher';
import { createNotification, NOTIFICATION_TYPES } from './notificationService';

class NotificationIntegrationService {
  
  /**
   * Crée une notification ET l'envoie automatiquement par email/SMS/push
   * selon les préférences utilisateur
   */
  async createAndSendNotification(notification) {
    try {
      console.log('🔄 INTEGRATION: Création et envoi notification pour:', notification.recipientId);
      
      // 1. Créer la notification dans Firestore
      const savedNotification = await createNotification(
        notification.recipientId,
        notification.title,
        notification.message,
        notification.type || NOTIFICATION_TYPES.INFO,
        notification.dossierId,
        notification.options || {}
      );
      
      // 2. Envoyer via les canaux externes (email, SMS, push)
      const dispatchResult = await notificationDispatcher.sendNotification({
        recipientId: notification.recipientId,
        title: notification.title,
        message: notification.message,
        urgency: notification.urgency || 'normal',
        type: notification.type || NOTIFICATION_TYPES.INFO,
        dossierId: notification.dossierId,
        dossierRef: notification.dossierRef,
        clientId: notification.clientId,
        senderId: notification.senderId
      });
      
      console.log('✅ INTEGRATION: Notification créée et envoyée:', {
        notificationId: savedNotification.id,
        dispatchResult: dispatchResult.deliveryResults
      });
      
      return {
        success: true,
        notification: savedNotification,
        deliveryResults: dispatchResult.deliveryResults
      };
      
    } catch (error) {
      console.error('❌ INTEGRATION: Erreur création/envoi notification:', error);
      throw error;
    }
  }
  
  /**
   * ✅ NOUVELLE MÉTHODE : Envoie seulement les notifications multi-canal (email, SMS, push)
   * Sans créer de notification Firestore
   */
  async sendMultiChannelNotification(notification) {
    try {
      console.log('📧 INTEGRATION: Envoi multi-canal uniquement pour:', notification.recipientId);
      
      // Envoyer SEULEMENT via les canaux externes (email, SMS, push)
      const dispatchResult = await notificationDispatcher.sendNotification({
        recipientId: notification.recipientId,
        title: notification.title,
        message: notification.message,
        urgency: notification.urgency || 'normal',
        type: notification.type || NOTIFICATION_TYPES.INFO,
        dossierId: notification.dossierId,
        dossierRef: notification.dossierRef,
        clientId: notification.clientId,
        senderId: notification.senderId
      });
      
      console.log('✅ INTEGRATION: Notification multi-canal envoyée:', dispatchResult.deliveryResults);
      
      return {
        success: true,
        deliveryResults: dispatchResult.deliveryResults
      };
      
    } catch (error) {
      console.error('❌ INTEGRATION: Erreur envoi multi-canal:', error);
      throw error;
    }
  }
  
  /**
   * ✅ CORRIGÉ : Notification lors d'une nouvelle étape timeline - MULTI-CANAL UNIQUEMENT
   * Le NotificationDispatcher crée déjà la notification Firestore !
   */
  async notifyTimelineStepUpdate(userId, stepTitle, stepDescription, dossierId, dossierRef) {
    return await this.sendMultiChannelNotification({
      recipientId: userId,
      title: `Étape mise à jour: ${stepTitle}`,
      message: `${stepDescription} - Dossier: ${dossierRef}`,
      type: NOTIFICATION_TYPES.TIMELINE_STEP,
      urgency: 'normal',
      dossierId,
      dossierRef
    });
  }
  
  /**
   * Notification lors d'un nouveau message
   */
  async notifyNewMessage(recipientId, senderName, messagePreview, dossierId, dossierRef) {
    return await this.createAndSendNotification({
      recipientId,
      title: `Nouveau message de ${senderName}`,
      message: `${messagePreview} - Dossier: ${dossierRef}`,
      type: NOTIFICATION_TYPES.MESSAGE,
      urgency: 'normal',
      dossierId,
      dossierRef
    });
  }
  
  /**
   * Notification d'échéance proche
   */
  async notifyDeadlineApproaching(userId, dossierRef, daysRemaining, dossierId) {
    const urgency = daysRemaining <= 2 ? 'urgent' : 'normal';
    return await this.createAndSendNotification({
      recipientId: userId,
      title: `Échéance proche - ${dossierRef}`,
      message: `Votre dossier ${dossierRef} arrive à échéance dans ${daysRemaining} jour(s).`,
      type: NOTIFICATION_TYPES.SYSTEM_ALERT,
      urgency,
      dossierId,
      dossierRef
    });
  }
  
  /**
   * Notification de création de dossier
   */
  async notifyDossierCreated(userId, dossierRef, dossierId) {
    return await this.createAndSendNotification({
      recipientId: userId,
      title: `Nouveau dossier créé: ${dossierRef}`,
      message: `Votre dossier ${dossierRef} a été créé avec succès. Vous pouvez maintenant le consulter.`,
      type: NOTIFICATION_TYPES.PROJECT_CREATED,
      urgency: 'normal',
      dossierId,
      dossierRef
    });
  }
  
  /**
   * Notification de document uploadé
   */
  async notifyDocumentUploaded(userId, documentName, dossierId, dossierRef) {
    return await this.createAndSendNotification({
      recipientId: userId,
      title: `Nouveau document: ${documentName}`,
      message: `Un nouveau document "${documentName}" a été ajouté à votre dossier ${dossierRef}.`,
      type: NOTIFICATION_TYPES.DOCUMENT_UPLOADED,
      urgency: 'normal',
      dossierId,
      dossierRef
    });
  }
}

export default new NotificationIntegrationService();